import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useApp } from '../AppContext';
import { PageHeader } from '../components/Layout';
import { Card, SectionTitle, Field, Spinner, ErrorNote, Empty, SelectMenu } from '../components/ui';
import { money, fmtDate, initials } from '../lib/format';

const TYPES = [
  { id: 'invoice',  label: 'Invoice',  sub: 'Bill a customer', path: 'invoice' },
  { id: 'receipt',  label: 'Receipt',  sub: 'Payment already received', path: 'receipt' },
  { id: 'proposal', label: 'Proposal', sub: 'Quote with signing', path: 'proposal' },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map(t => [t.id, t.label]));
const PATH = Object.fromEntries(TYPES.map(t => [t.id, t.path]));

/**
 * Invoices, receipts and proposals made on their own — no lead, no pipeline.
 * Pick a type, say who it is for, and land in the usual builder.
 */
export default function Documents() {
  const navigate = useNavigate();
  const { toast, run } = useApp();
  const { data: customers } = useAsync(() => api.get('/customers'), []);
  const { data: recent, error, loading, reload } = useAsync(() => api.get('/quick'), []);

  const [type, setType] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ name: '', phone: '', email: '', address1: '', city: '', state: '', zip: '' });
  const set = (k) => (e) => setF(prev => ({ ...prev, [k]: e.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    if (!navigator.onLine) { toast('Creating a document needs a connection.'); return; }
    setBusy(true);
    await run(async () => {
      const r = await api.post('/quick', customerId ? { type, customerId } : { type, customer: f });
      navigate(`/project/${r.projectId}/${PATH[type]}`);
    });
    setBusy(false);
  }

  return (
    <>
      <PageHeader title="Documents" sub="Invoices, receipts and proposals — no lead needed" />
      <div className="view">
        <Card>
          <SectionTitle>Make a new one</SectionTitle>
          <div className="seg" role="group" aria-label="Document type" style={{ marginTop: 'var(--s-3)' }}>
            {TYPES.map(t => (
              <button key={t.id} type="button" aria-pressed={type === t.id} onClick={() => setType(t.id)}>
                {t.label}
              </button>
            ))}
          </div>
          {type ? <p className="sb" style={{ marginTop: 'var(--s-2)' }}>{TYPES.find(t => t.id === type).sub}</p> : null}

          {type ? (
            <form className="col" style={{ gap: 'var(--s-3)', marginTop: 'var(--s-3)' }} onSubmit={onSubmit}>
              <Field label="Who is it for?" id="qcust">
                <SelectMenu id="qcust" value={customerId} onChange={setCustomerId}
                  options={[
                    { value: '', label: '— New customer —' },
                    ...(customers || []).map(c => ({ value: c.id, label: c.name })),
                  ]} />
              </Field>
              {!customerId ? (
                <>
                  <Field label="Customer name *" id="qname">
                    <input id="qname" className="fi" required value={f.name} onChange={set('name')} />
                  </Field>
                  <div className="row-fields">
                    <Field label="Phone" id="qphone">
                      <input id="qphone" className="fi" type="tel" value={f.phone} onChange={set('phone')} />
                    </Field>
                    <Field label="Email" id="qemail" hint="Needed to email the document">
                      <input id="qemail" className="fi" type="email" value={f.email} onChange={set('email')} />
                    </Field>
                  </div>
                  <Field label="Address" id="qa1" hint="Optional — shown on the document">
                    <input id="qa1" className="fi" value={f.address1} onChange={set('address1')} />
                  </Field>
                  <div className="row-fields" style={{ '--cols': 3 }}>
                    <Field label="City" id="qcity"><input id="qcity" className="fi" value={f.city} onChange={set('city')} /></Field>
                    <Field label="State" id="qstate"><input id="qstate" className="fi" value={f.state} onChange={set('state')} /></Field>
                    <Field label="Zip" id="qzip"><input id="qzip" className="fi" value={f.zip} onChange={set('zip')} /></Field>
                  </div>
                </>
              ) : null}
              <button className="btn pri" type="submit" disabled={busy}>Continue</button>
            </form>
          ) : null}
        </Card>

        <SectionTitle>Recent</SectionTitle>
        {loading ? <Spinner label="Loading documents" /> : null}
        {error ? <ErrorNote onRetry={reload}>{error}</ErrorNote> : null}
        {recent ? (
          <Card tight>
            {recent.length ? recent.map(d => (
              <Link key={d.type + d.id} className="listrow" to={`/project/${d.projectId}/${PATH[d.type]}`}>
                <span className="ava" aria-hidden="true">{initials(d.customer?.name || '?')}</span>
                <span className="nm">
                  {d.customer?.name || 'Unknown'}
                  <span className="sb" style={{ display: 'block', fontWeight: 400 }}>
                    {TYPE_LABEL[d.type]} #{d.number} · {fmtDate(d.createdAt)} · {String(d.status || 'draft').replace('_', ' ')}
                  </span>
                </span>
                <span className="nm">{money(d.total)}</span>
              </Link>
            )) : <Empty>Nothing yet. Pick a type above to start.</Empty>}
          </Card>
        ) : null}
      </div>
    </>
  );
}
